/**
 * Customer Manager Types
 *
 * Type definitions for the Customer Manager feature.
 * Aligned with the backend Identity Service Customer schema —
 * see `packages/schemas/identity/customer.json` and
 * `packages/go/identity/customer.go` for the source of truth.
 *
 * IMPORTANT: only fields that actually exist on the backend Customer
 * record are typed as required / non-optional here. Stream-derived /
 * computed fields are kept on a separate display type so we never
 * confuse "what the API gave us" with "what we display".
 */

// ── Core Customer Types ─────────────────────────────────────────────────────

/**
 * Customer lifecycle status as defined by the backend.
 * See `packages/schemas/identity/customer.json#properties.status.enum`.
 *
 * The backend may also return an empty string for legacy/admin-created
 * customers that have not yet had a lifecycle status assigned. We model
 * that explicitly as the `''` literal so consumers must handle it.
 */
export type CustomerStatus = 'lead' | 'registered' | 'active' | 'churned' | ''

/**
 * Customer acquisition origin (immutable first-touch data).
 */
export interface CustomerOrigin {
  channel?: string
  app_id?: string
  consumer_id?: string
  auth_method?: string
  campaign_id?: string
  referral_code_id?: string
  utm?: Record<string, string>
}

/**
 * External identifier reference (Stripe, RevenueCat, OneSignal, etc.).
 */
export interface CustomerExternalIdentifier {
  provider: string
  [key: string]: unknown
}

/**
 * Push notification token registered for the customer.
 */
export interface CustomerPushToken {
  token: string
  platform?: string
  last_seen?: number
}

/**
 * Feature - a capability or content access level within an app.
 * See `packages/schemas/catalog/feature.json` for the canonical schema.
 */
export interface Feature {
  id: string // Pattern: ^feat_[a-f0-9]+$
  object: 'feature'
  app_id: string
  lookup_key: string // Immutable code reference (e.g., '4k_streaming')
  name: string // Human-readable name
}

/**
 * Entitlement - a bundle of features granted to a customer.
 * See `packages/schemas/catalog/entitlement.json` for the canonical schema.
 *
 * The catalog response also includes optional display metadata (name,
 * description, icon) — typed loosely so we can surface whichever fields
 * the catalog returns without forcing a tight coupling to its OpenAPI.
 */
export interface Entitlement {
  id: string // Pattern: ^ent_[a-f0-9]+$
  object: 'entitlement'
  feature_ids: string[]
  description_generated?: string // Auto-generated summary (e.g., 'Full Access + 4K')
  display?: {
    name?: string
    description?: string
    icon?: string
  }
  metadata?: Record<string, string>
  created?: number
  updated?: number
}

/**
 * Source for an entitlement grant — how the grant came to exist.
 * See `services/identity/api/openapi.yaml#/components/schemas/EntitlementGrant`.
 */
export type EntitlementGrantSourceType =
  | 'purchase'
  | 'manual_grant'
  | 'promotion'
  | 'trial'
  | 'withdrawal'
  | 'group_share'
  | 'referral_reward'

/**
 * Entitlement grant - the record linking a customer to an entitlement,
 * with time-bound validity and an audit trail.
 */
export interface EntitlementGrant {
  id: string // Pattern: ^egr_[a-zA-Z0-9]+$
  object: 'entitlement_grant'
  created: number
  updated: number
  customer_id: string
  entitlement_id: string
  valid_from: number // Unix timestamp
  valid_to: number // Unix timestamp
  source_id?: string
  source_type?: EntitlementGrantSourceType
  note?: string
  staff_id?: string
  features?: string[] // Feature IDs included in this grant
}

/**
 * Request body for POST /v1/admin/entitlement-grants.
 */
export interface CreateEntitlementGrantRequest {
  customer_id: string
  entitlement_id: string
  valid_to: number // Required — Unix timestamp when the grant expires
  valid_from?: number // Optional — defaults to now
  source_type?: EntitlementGrantSourceType
  note?: string
}

/**
 * Catalog list response for `GET /v1/entitlements`.
 */
export interface EntitlementListResponse {
  object: 'list'
  data: Entitlement[]
  has_more: boolean
}

/**
 * Customer entity matching the backend Customer schema.
 *
 * Only `id`, `object`, `created`, `updated`, and `email` are required by
 * the backend. Everything else is optional or may be empty.
 */
export interface Customer {
  // Core (always present)
  id: string
  object: 'customer'
  created: number // Unix timestamp
  updated: number // Unix timestamp
  email: string

  // Common but optional
  name?: string
  phone?: string
  avatar_url?: string
  status?: CustomerStatus
  delinquent?: boolean

  // Activity / verification
  last_seen_at?: number
  email_verified?: boolean
  email_verified_at?: number

  // First-touch / consumer tracking
  origin?: CustomerOrigin
  consumer_sightings?: Record<string, number> // consumer_id -> first-seen unix ts

  // Preferences
  locale?: string
  timezone?: string
  currency?: string

  // Consent & tagging
  consent_granted?: boolean
  consent_timestamp?: number
  tags?: string[]

  // External references
  external_ids?: Record<string, string>
  external_identifiers?: CustomerExternalIdentifier[]

  // Mode / metadata / api version
  livemode?: boolean
  api_version?: string
  metadata?: Record<string, string>

  // Soft delete
  deleted?: boolean
  deleted_at?: number

  // Push tokens (rarely on root, included when fetched directly)
  push_tokens?: CustomerPushToken[]

  // Expandable sub-resources (only present when expand[]=… is requested)
  purchases?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
  charges?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
  transactions?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
  entitlements?: { object: 'list'; data: Entitlement[]; has_more?: boolean; url?: string }
  features?: { object: 'list'; data: Feature[]; has_more?: boolean; url?: string }
  attributions?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
  campaigns?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
  redemptions?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
  emails?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
  retention_messages?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
  segments?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
  experiments?: { object: 'list'; data: unknown[]; has_more?: boolean; url?: string }
}

/**
 * Customer list response from GET /v1/customers.
 *
 * The endpoint returns `next_page_token` as the opaque cursor to feed back
 * via the `page_token` query parameter. An empty/missing token indicates the
 * last page (i.e. `has_more` is also `false`).
 */
export interface CustomerListResponse {
  data: Customer[]
  has_more: boolean
  object: 'list'
  next_page_token?: string
}

/**
 * Customer create request.
 */
export interface CustomerCreateRequest {
  email: string
  name?: string
  metadata?: Record<string, string>
  send_welcome_email?: boolean
}

/**
 * Customer update request for PATCH /v1/customers/{id}.
 *
 * Backend accepts: name, status, metadata, tags, consent_granted,
 * consent_timestamp (per `services/identity/api/openapi.yaml#/components/schemas/CustomerUpdateRequest`).
 *
 * `email` is intentionally NOT here — the backend does not allow editing it
 * via this endpoint and the frontend must not pretend otherwise.
 */
export interface CustomerUpdateRequest {
  name?: string
  status?: Exclude<CustomerStatus, ''>
  metadata?: Record<string, string>
  tags?: string[]
  consent_granted?: boolean
  consent_timestamp?: number
}

// ── Customer Narrative ──────────────────────────────────────────────────────

/**
 * Confidence level for a belief or value assessment.
 * Mirrors `packages/go/narrative.Confidence`.
 */
export type Confidence = 'low' | 'medium' | 'high'

/**
 * Chapter — current narrative phase of a customer's journey.
 * Mirrors `packages/go/narrative.Chapter`.
 */
export interface NarrativeChapter {
  name: string
  started: string // ISO8601 date
  themes: string[]
  summary: string // 150-300 words
}

/**
 * Belief — a working hypothesis about the customer.
 * Mirrors `packages/go/narrative.Belief`.
 */
export interface Belief {
  statement: string
  confidence: Confidence
  implications: string
  evidence_refs: string[]
}

/**
 * CommunicationGuidance — actionable guidance for customer communications.
 * Mirrors `packages/go/narrative.CommunicationGuidance`.
 */
export interface CommunicationGuidance {
  effective_approaches?: string
  ineffective_approaches?: string
  tone_notes?: string
  hard_constraints?: string[]
}

/**
 * Customer narrative - qualitative understanding from GET /v1/customers/{id}/narrative.
 *
 * Mirrors `packages/go/narrative.CustomerNarrative` — fields that are objects
 * on the backend MUST be objects here (rendering them as strings will throw
 * "Objects are not valid as a React child").
 */
export interface CustomerNarrative {
  chapter?: NarrativeChapter
  identity_summary?: string
  beliefs?: Belief[]
  communication_guidance?: CommunicationGuidance
  updated_at?: number
}

// ── Customer Opportunity ────────────────────────────────────────────────────

/**
 * Risk type categorization for risk monitors.
 */
export type RiskType =
  | 'engagement_decline'
  | 'payment_risk'
  | 'churn_signal'
  | 'satisfaction_drop'

/**
 * Risk status for risk monitors.
 */
export type RiskStatus = 'active' | 'watching' | 'clear'

/**
 * Risk monitor - tracks signals that could indicate problems.
 */
export interface RiskMonitor {
  risk_type: RiskType
  status: RiskStatus
  description: string
  trigger_conditions: string
  last_checked: string // ISO8601
  current_reading: string
  recommended_response: string
}

/**
 * Categorisation of expansion opportunities.
 * Mirrors `packages/go/narrative.OpportunityType`.
 */
export type OpportunityType =
  | 'app_adoption'
  | 'household_expansion'
  | 'tier_upgrade'
  | 'feature_adoption'

/**
 * When an opportunity should be acted on.
 * Mirrors `packages/go/narrative.OpportunityTiming`.
 */
export type OpportunityTiming =
  | 'after_next_milestone'
  | 'immediate'
  | 'quarterly_review'
  | 'seasonal'

/**
 * Categorisation of intervention opportunities.
 * Mirrors `packages/go/narrative.InterventionType`.
 */
export type InterventionType =
  | 'celebration'
  | 're-engagement'
  | 'education'
  | 'upgrade_prompt'
  | 'retention'

/**
 * Channel used to deliver an intervention.
 * Mirrors `packages/go/narrative.DeliveryChannel`.
 */
export type DeliveryChannel = 'app' | 'email' | 'push' | 'in_app_message'

/**
 * Priority of a recommended action.
 */
export type Priority = 'high' | 'medium' | 'low'

/**
 * Expansion opportunity - a specific growth opportunity for the customer.
 * Mirrors `packages/go/narrative.ExpansionOpportunity`.
 */
export interface ExpansionOpportunity {
  opportunity_type: OpportunityType
  target: string
  psychological_hook: string
  hypothesis: string
  success_criteria: string
  rationale: string
  approach: string
  timing: OpportunityTiming
  timing_rationale: string
  constraints: string[]
  estimated_incremental_value: number
  confidence: Confidence
}

/**
 * Intervention opportunity - a pre-planned intervention ready to execute.
 * Mirrors `packages/go/narrative.InterventionOpportunity`.
 */
export interface InterventionOpportunity {
  intervention_type: InterventionType
  trigger: string
  psychological_hook: string
  hypothesis: string
  success_criteria: string
  approach: string
  channel: DeliveryChannel
  constraints: string[]
  priority: Priority
  window: string
}

/**
 * Outcome of an executed intervention.
 * Mirrors `packages/go/narrative.InterventionOutcome`.
 */
export type InterventionOutcome =
  | 'engaged'
  | 'ignored'
  | 'negative_response'
  | 'unknown'

/**
 * InterventionLog - historical record of an intervention executed against the
 * customer. Each entry is anchored to a `strategy_id` (strat_<24 hex chars>)
 * generated by the Gamified Campaign Synthesizer (CI-20), letting us trace a
 * single strategy through time and across multiple touchpoints.
 *
 * Mirrors `packages/go/narrative.InterventionLog`.
 */
export interface InterventionLog {
  date: string // ISO8601
  strategy_id: string // strat_<24 hex chars>
  intervention_type: string
  channel: DeliveryChannel
  outcome: InterventionOutcome
  notes: string
}

/**
 * Customer opportunity record from GET /v1/customers/{id}/opportunity.
 *
 * Mirrors `packages/go/narrative.BusinessOpportunityRecord`. NOTE: the legacy
 * top-level fields (psychological_hook, hypothesis, success_criteria) are kept
 * for backward compatibility with any callers / older snapshots; on the real
 * backend record those live inside each ExpansionOpportunity.
 */
export interface CustomerOpportunity {
  // Legacy / convenience fields — not present on backend BusinessOpportunityRecord.
  psychological_hook?: string
  hypothesis?: string
  success_criteria?: string
  strategy_id?: string
  updated_at?: number

  // Real backend fields.
  expansion_opportunities?: ExpansionOpportunity[]
  intervention_opportunities?: InterventionOpportunity[]
  intervention_log?: InterventionLog[]
  risk_monitors?: RiskMonitor[]
}

// ── Customer Events ─────────────────────────────────────────────────────────

/**
 * Customer event from GET /v1/admin/customers/{id}/events.
 */
export interface CustomerEvent {
  id: string
  type: string
  created: number
  source: string
  data?: Record<string, unknown>
}

/**
 * Customer events list response.
 */
export interface CustomerEventsResponse {
  data: CustomerEvent[]
  has_more: boolean
}

// ── Customer Purchases ──────────────────────────────────────────────────────

/**
 * Customer purchase from GET /v1/admin/customers/{id}/purchases.
 */
export interface CustomerPurchase {
  id: string
  product_id: string
  product_name: string
  amount: number
  currency: string
  status: 'completed' | 'refunded' | 'pending'
  created: number
}

/**
 * Customer purchases list response.
 */
export interface CustomerPurchasesResponse {
  data: CustomerPurchase[]
  has_more: boolean
  total_spend: number
}

// ── Customer Source Records ─────────────────────────────────────────────────

/**
 * Customer source record from GET /v1/admin/customers/{id}/source-records.
 */
export interface CustomerSourceRecord {
  source: string
  external_id: string
  data?: Record<string, unknown>
  synced_at?: number
}

/**
 * Customer source records response.
 */
export interface CustomerSourceRecordsResponse {
  data: CustomerSourceRecord[]
}

// ── Filter & Sort Types ─────────────────────────────────────────────────────

/**
 * Filter parameters for customer list.
 *
 * `status` accepts `Exclude<CustomerStatus, ''>` because the empty-string
 * sentinel is never a valid filter value (it represents "no status set",
 * which is filtered via a synthetic `unknown` option in the UI).
 */
export interface CustomerFilters {
  q?: string // Search query
  status?: Array<Exclude<CustomerStatus, ''> | 'unknown'>
  domain?: string[]
  /**
   * Tag filter — matches customers whose `tags` array contains ALL listed
   * values. The identity service writes `new_user` on signup and reserves
   * `high_value`, `at_risk`, `churned` for purchase/billing listeners. Custom
   * application tags are also supported.
   */
  tags?: string[]
  /**
   * Email verification filter:
   *   - `true`  → only customers with `email_verified === true`
   *   - `false` → only customers without a verified email
   *   - `undefined` → no filter
   */
  email_verified?: boolean
  joined_from?: string // ISO date
  joined_to?: string // ISO date
  active_from?: string // ISO date
  active_to?: string // ISO date
}

/**
 * Sort state for customer list.
 *
 * `last_seen_at` matches the backend field name. We sort by *effective*
 * last activity — see `effectiveLastSeenAt()` in `./activity.ts` — which
 * falls back to consumer sightings / verification / created when
 * `last_seen_at` itself is unset.
 */
export interface CustomerSortState {
  column:
    | 'name'
    | 'email'
    | 'created'
    | 'updated'
    | 'last_seen_at'
    | 'status'
    | null
  direction: 'asc' | 'desc' | null
}

// ── Detail Cache Types ──────────────────────────────────────────────────────

/**
 * Cached customer detail including nested resources.
 */
export interface CustomerDetail {
  customer: Customer
  narrative?: CustomerNarrative | null
  opportunity?: CustomerOpportunity | null
  events?: CustomerEvent[]
  purchases?: CustomerPurchase[]
  sourceRecords?: CustomerSourceRecord[]
  lastFetchedAt: number
}

// ── Inspector Tab Types ─────────────────────────────────────────────────────

export type CustomerInspectorTab =
  | 'overview'
  | 'narrative'
  | 'opportunity'
  | 'entitlements'
  | 'timeline'
  | 'edit'
  | 'purchases'

// ── Saved Filter Presets ────────────────────────────────────────────────────

export interface CustomerFilterPreset {
  id: string
  label: string
  filters: CustomerFilters
  isDefault?: boolean
}

/**
 * Default filter presets surfaced as quick-select chips above the customer
 * list.
 *
 * Backed by fields the identity service actually populates today (verified
 * against `kodeon-backend-prod` 2026-05-11):
 *   - `tags` contains `new_user` for ~99% of customers (set on signup by
 *     `services/identity/backend/internal/events/tag_listeners.go`)
 *   - `email_verified === true` is the only signal of post-signup engagement
 *     that currently fires in production
 *   - `created` is always set, so a 7-day "recently signed up" range works
 *
 * The previous chip set (`Active` / `Leads` / `Churned`) filtered on
 * `customer.status`, which is unset for every production customer today. It
 * was reinstated only after the identity service starts writing those values
 * on lifecycle transitions.
 *
 * The "Recently signed up" preset is dynamic — its `joined_from` is computed
 * via {@link getDefaultCustomerPresets} relative to "now". The module-level
 * constant captures the value at import time and is used by code that does
 * not need a fresh window on every read (the chip UI calls the function).
 */
export function getDefaultCustomerPresets(now: Date = new Date()): CustomerFilterPreset[] {
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  // YYYY-MM-DD slice keeps the URL-safe ISO date format the rest of the
  // filter pipeline already uses for `joined_from`.
  const sevenDaysAgoIso = sevenDaysAgo.toISOString().slice(0, 10)

  return [
    {
      id: 'all',
      label: 'All',
      filters: {},
      isDefault: true,
    },
    {
      id: 'new_users',
      label: 'New users',
      filters: { tags: ['new_user'] },
    },
    {
      id: 'email_verified',
      label: 'Email verified',
      filters: { email_verified: true },
    },
    {
      id: 'recently_signed_up',
      label: 'Last 7 days',
      filters: { joined_from: sevenDaysAgoIso },
    },
  ]
}

export const DEFAULT_CUSTOMER_PRESETS: CustomerFilterPreset[] = getDefaultCustomerPresets()

// ── Deletions Queue ──────────────────────────────────────────────────────────

/**
 * A single customer in the account-deletion pipeline. PII-minimised: during
 * cooldown the email/name are still intact; after purge only the anonymised
 * id, retained Stripe id, and timestamps remain. Timestamps are Unix seconds.
 */
export interface DeletionRow {
  id: string
  email?: string
  name?: string
  requested_at?: number
  scheduled_deletion_date?: number
  deleted_at?: number
  stripe_id?: string
}

/**
 * The account-deletion queue grouped by lifecycle bucket, as returned by
 * GET /v1/customers/deletions.
 */
export interface DeletionQueue {
  object: 'deletion_queue'
  ready: DeletionRow[]
  cooldown: DeletionRow[]
  deleted: DeletionRow[]
  counts: {
    ready: number
    cooldown: number
    deleted: number
  }
}
